import React from "react";
import { useAnalyticsOverview, usePredictionSummary } from "../../../hooks/useDemandAnalytics";
import StatCard from "../../../components/admin/StatCard";
import BarChart from "../../../components/admin/BarChart";
import HorizontalBar from "../../../components/admin/HorizontalBar";

const OverviewTab = () => {
  const { data, isLoading } = useAnalyticsOverview(30);
  const { data: predSummary } = usePredictionSummary();

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="skeleton h-24 rounded-lg"></div>
        ))}
      </div>
    );
  }

  const d = data?.data;
  if (!d) return <div className="text-gray-500">No data available</div>;

  const events = d.events || {};
  const bloodDemand = d.bloodDemand || {};
  const locations = d.locations || {};
  const summary = predSummary?.data || {};

  const demandChartData = (bloodDemand.demandByGroup || []).map((item) => ({
    label: item.group,
    value: item.count,
  }));

  const topDistrictData = (locations.donorsByDistrict || []).slice(0, 10).map((item) => ({
    label: item.district,
    value: item.count,
  }));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Events (30d)"
          value={events.totalEvents?.toLocaleString()}
          icon="fa-bolt"
          color="bg-red-50"
          textColor="text-red-600"
        />
        <StatCard
          title="Active Users"
          value={events.uniqueUsers?.toLocaleString()}
          icon="fa-users"
          color="bg-blue-50"
          textColor="text-blue-600"
        />
        <StatCard
          title="Blood Requests"
          value={bloodDemand.fulfillmentRate?.total?.toLocaleString()}
          icon="fa-hand-holding-droplet"
          color="bg-orange-50"
          textColor="text-orange-600"
        />
        <StatCard
          title="Fulfillment Rate"
          value={`${bloodDemand.fulfillmentRate?.rate || 0}%`}
          icon="fa-check-circle"
          color="bg-green-50"
          textColor="text-green-600"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card bg-base-100 shadow">
          <div className="card-body">
            <h2 className="card-title text-sm">Blood Group Demand</h2>
            <BarChart
              data={demandChartData}
              labelKey="label"
              valueKey="value"
              color="bg-red-500"
              height="h-40"
            />
          </div>
        </div>

        <div className="card bg-base-100 shadow">
          <div className="card-body">
            <h2 className="card-title text-sm">Top Districts by Donors</h2>
            <HorizontalBar items={topDistrictData} color="bg-blue-500" />
          </div>
        </div>
      </div>

      {summary.hasEnoughData && (
        <div className="card bg-base-100 shadow">
          <div className="card-body">
            <h2 className="card-title text-sm">Prediction Summary</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-2">
              <div>
                <span className="text-xs text-gray-500">Overall Level</span>
                <div className="font-bold text-lg">
                  <span className={`badge ${
                    summary.overallDemandLevel === "CRITICAL"
                      ? "badge-error"
                      : summary.overallDemandLevel === "HIGH"
                      ? "badge-warning"
                      : summary.overallDemandLevel === "MEDIUM"
                      ? "badge-info"
                      : "badge-success"
                  }`}>
                    {summary.overallDemandLevel}
                  </span>
                </div>
              </div>
              <div>
                <span className="text-xs text-gray-500">Top Requested Group</span>
                <div className="font-bold">{summary.topRequestedGroup}</div>
              </div>
              <div>
                <span className="text-xs text-gray-500">Top Demand Area</span>
                <div className="font-bold">{summary.topDemandArea}</div>
              </div>
              <div>
                <span className="text-xs text-gray-500">Critical Groups</span>
                <div className="font-bold">
                  {summary.criticalGroups?.length
                    ? summary.criticalGroups.join(", ")
                    : "None"}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {summary.hasEnoughData === false && (
        <div className="alert alert-info">
          <i className="fa-solid fa-circle-info"></i>
          <span>
            Insufficient historical data for reliable predictions. More data will
            improve prediction accuracy over time.
          </span>
        </div>
      )}

      <div className="card bg-base-100 shadow">
        <div className="card-body">
          <h2 className="card-title text-sm">Events by Day (30d)</h2>
          <BarChart
            data={(events.eventsByDay || []).map((d) => ({
              label: d.date?.slice(5) || d.date,
              value: d.count,
            }))}
            labelKey="label"
            valueKey="value"
            color="bg-purple-500"
            height="h-32"
          />
        </div>
      </div>
    </div>
  );
};

export default OverviewTab;
